import { getMedicalRecordAttachmentsObject } from './medicalRecordStatus';

export const INITIAL_ANAMNESIS_KIND = 'initial_anamnesis';
export const CLINICAL_HISTORY_KIND = 'clinical_history';
export const STRUCTURED_ANAMNESIS_KIND = 'anamnesis';

export interface AnamnesisSections {
  chiefComplaint: string;
  currentIllnessHistory: string;
  personalHistory: string;
  surgicalHistory: string;
  familyHistory: string;
  medications: string;
  allergies: string;
  habitsAndSocialContext: string;
  additionalNotes: string;
}

export type AnamnesisSectionKey = keyof AnamnesisSections;

export const ANAMNESIS_SECTION_DEFINITIONS: ReadonlyArray<{
  key: AnamnesisSectionKey;
  label: string;
  shortLabel?: string;
  placeholder: string;
}> = [
  {
    key: 'chiefComplaint',
    label: 'Queixa principal',
    placeholder: 'Descreva a principal queixa relatada pelo paciente.',
  },
  {
    key: 'currentIllnessHistory',
    label: 'História da moléstia atual (HMA)',
    shortLabel: 'HMA',
    placeholder: 'Início, evolução, sintomas, fatores de melhora ou piora e tratamentos já realizados.',
  },
  {
    key: 'personalHistory',
    label: 'Antecedentes pessoais (AP)',
    shortLabel: 'AP',
    placeholder: 'Doenças prévias, condições associadas e outros antecedentes pessoais relevantes.',
  },
  {
    key: 'surgicalHistory',
    label: 'Antecedentes cirúrgicos (AC)',
    shortLabel: 'AC',
    placeholder: 'Cirurgias, procedimentos e respectivas datas, quando conhecidas.',
  },
  {
    key: 'familyHistory',
    label: 'Antecedentes familiares (AF)',
    shortLabel: 'AF',
    placeholder: 'Doenças e condições relevantes na família.',
  },
  {
    key: 'medications',
    label: 'Medicamentos em uso',
    placeholder: 'Medicamentos, doses e frequência, quando disponíveis.',
  },
  {
    key: 'allergies',
    label: 'Alergias',
    placeholder: 'Alergias medicamentosas ou outras alergias conhecidas.',
  },
  {
    key: 'habitsAndSocialContext',
    label: 'Hábitos e contexto social',
    placeholder: 'Tabagismo, atividade física, ocupação, rotina e contexto social relevante.',
  },
  {
    key: 'additionalNotes',
    label: 'Observações complementares',
    placeholder: 'Outras informações importantes para o cuidado do paciente.',
  },
];

export const createEmptyAnamnesisSections = (): AnamnesisSections => ({
  chiefComplaint: '',
  currentIllnessHistory: '',
  personalHistory: '',
  surgicalHistory: '',
  familyHistory: '',
  medications: '',
  allergies: '',
  habitsAndSocialContext: '',
  additionalNotes: '',
});

export type ClinicalHistoryType =
  | 'initial_anamnesis'
  | 'anamnesis_update'
  | 'anamnesis'
  | 'hma'
  | 'personal_history'
  | 'family_history'
  | 'surgical_history'
  | 'medications_allergies'
  | 'clinical_update'
  | 'other';

export const CLINICAL_HISTORY_TYPE_LABELS: Record<ClinicalHistoryType, string> = {
  initial_anamnesis: 'Anamnese inicial',
  anamnesis_update: 'Atualização da anamnese',
  anamnesis: 'Anamnese',
  hma: 'História da moléstia atual (HMA)',
  personal_history: 'Antecedentes pessoais (AP)',
  family_history: 'Antecedentes familiares (AF)',
  surgical_history: 'Histórico cirúrgico',
  medications_allergies: 'Medicações e alergias',
  clinical_update: 'Atualização do histórico clínico',
  other: 'Outro registro clínico',
};

export const isInitialAnamnesisRecord = (recordAttachments: unknown): boolean => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  return attachments.clinicalDocumentKind === INITIAL_ANAMNESIS_KIND;
};

export const isStructuredAnamnesisRecord = (recordAttachments: unknown): boolean => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  return attachments.clinicalDocumentKind === STRUCTURED_ANAMNESIS_KIND;
};

export const isClinicalHistoryRecord = (recordAttachments: unknown): boolean => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  return (
    attachments.clinicalDocumentKind === INITIAL_ANAMNESIS_KIND ||
    attachments.clinicalDocumentKind === CLINICAL_HISTORY_KIND ||
    attachments.clinicalDocumentKind === STRUCTURED_ANAMNESIS_KIND
  );
};

export const getAnamnesisSections = (recordAttachments: unknown): AnamnesisSections => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  const rawSections = attachments.anamnesisSections;
  const sections = createEmptyAnamnesisSections();

  if (!rawSections || typeof rawSections !== 'object' || Array.isArray(rawSections)) {
    return sections;
  }

  for (const { key } of ANAMNESIS_SECTION_DEFINITIONS) {
    const value = (rawSections as Record<string, unknown>)[key];
    sections[key] = typeof value === 'string' ? value : '';
  }

  return sections;
};

export const hasAnamnesisContent = (sections: AnamnesisSections): boolean =>
  ANAMNESIS_SECTION_DEFINITIONS.some(({ key }) => sections[key].trim().length > 0);

export const formatAnamnesisSections = (sections: AnamnesisSections): string =>
  ANAMNESIS_SECTION_DEFINITIONS
    .map(({ key, label }) => ({ label, value: sections[key].trim() }))
    .filter(({ value }) => value.length > 0)
    .map(({ label, value }) => `${label}:\n${value}`)
    .join('\n\n');

export const getClinicalHistoryType = (recordAttachments: unknown): ClinicalHistoryType => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  if (attachments.clinicalDocumentKind === INITIAL_ANAMNESIS_KIND) {
    return Number(attachments.clinicalDocumentVersion) > 1
      ? 'anamnesis_update'
      : 'initial_anamnesis';
  }

  if (attachments.clinicalDocumentKind === STRUCTURED_ANAMNESIS_KIND) {
    return Number(attachments.clinicalDocumentVersion) > 1
      ? 'anamnesis_update'
      : 'anamnesis';
  }

  const type = attachments.clinicalHistoryType;
  return typeof type === 'string' && type in CLINICAL_HISTORY_TYPE_LABELS
    ? (type as ClinicalHistoryType)
    : 'other';
};
